import React, { useEffect, useState } from 'react';
import { X, FileText, Calendar, CheckCircle2, Lock, User, LogOut, Download, ArrowRight, RotateCcw, Shield, AlertTriangle, HelpCircle } from 'lucide-react';
import { useAuth } from './AuthContext';
import { API_BASE } from '../../services/api';

interface EstateSummary {
    id: string;
    deceased_name: string | null;
    date_of_death: string | null;
    status: 'draft' | 'unlocked';
    created_at: string;
    updated_at: string;
    current_step: number;
    question_count: number;
}

interface Props {
    onClose: () => void;
    onOpenCase: (estateId: string) => void;
    onNewCase: () => void;
}

import { PRICE_LABEL as PRICE } from '../lib/commercialCopy';

function fmtDate(iso: string): string {
    try {
        return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return ''; }
}

function fmtUpdated(iso: string): string {
    try {
        const d = new Date(iso);
        const now = new Date();
        const diffM = Math.floor((now.getTime() - d.getTime()) / (1000 * 60));
        if (diffM < 1) return 'Just now';
        if (diffM < 60) return `${diffM}m ago`;
        const diffH = Math.floor(diffM / 60);
        if (diffH < 24) return `${diffH}h ago`;
        return fmtDate(iso);
    } catch { return ''; }
}

export const UserDashboard: React.FC<Props> = ({ onClose, onOpenCase, onNewCase }) => {
    const { user, logout } = useAuth();
    const [estates, setEstates] = useState<EstateSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [upgradeError, setUpgradeError] = useState<string | null>(null);

    const [resetTargetId, setResetTargetId] = useState<string | null>(null);
    const [freeForAll, setFreeForAll] = useState(false);

    useEffect(() => {
        fetch(`${API_BASE}/api/config`)
            .then(r => r.ok ? r.json() : Promise.resolve({ freeForAll: false }))
            .then(d => setFreeForAll(!!d.freeForAll))
            .catch(() => {});
    }, []);

    // Consumer model: each user has a single saved audit. If legacy data
    // contains multiple estate rows we surface only the most recently
    // touched one — older rows stay in the database (we don't delete them)
    // but are hidden from the consumer dashboard. Unlocked > drafts so a
    // paid report is always preferred over a stray older draft.
    const sortedEstates = [...estates].sort((a, b) => {
        if (a.status !== b.status) return a.status === 'unlocked' ? -1 : 1;
        return b.updated_at.localeCompare(a.updated_at);
    });
    const primaryEstate = sortedEstates[0] ?? null;

    useEffect(() => {
        fetch(`${API_BASE}/api/estates`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : Promise.reject(r.status))
            .then(setEstates)
            .catch(() => setError('Could not load your estates'))
            .finally(() => setLoading(false));
    }, []);

    const handleReset = async () => {
        if (!resetTargetId) return;
        setResetting(true);
        try {
            await fetch(`${API_BASE}/api/estates/${resetTargetId}`, { method: 'DELETE', credentials: 'include' });
            setEstates(list => list.filter(e => e.id !== resetTargetId));
            setShowResetConfirm(false);
            setResetTargetId(null);
        } catch { /* silent */ }
        finally { setResetting(false); }
    };

    const handleUpgrade = async (targetEstateId?: string) => {
        const id = targetEstateId ?? sortedEstates.find(e => e.status !== 'unlocked')?.id ?? sortedEstates[0]?.id;
        if (!id) return;
        setUpgradeError(null);
        try {
            const currentUrl = window.location.origin + window.location.pathname;
            const res = await fetch(`${API_BASE}/api/billing/checkout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    priceId: 'full_report_unlock',
                    successUrl: `${currentUrl}?payment=success&estateId=${encodeURIComponent(id)}`,
                    cancelUrl: currentUrl + '?payment=cancelled',
                    metadata: { estateId: id },
                }),
            });
            const result = await res.json();
            if (result.url) {
                window.location.href = result.url;
            } else if (result.error === 'Billing is not enabled') {
                setUpgradeError('Payment is not yet configured. The full report will be available once Stripe is set up.');
            } else {
                setUpgradeError(result.error || 'Could not start checkout');
            }
        } catch {
            setUpgradeError('Could not connect to payment service. Please try again.');
        }
    };

    const handleLogout = async () => {
        await logout();
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="px-6 pt-5 pb-4 border-b border-slate-100 shrink-0">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-bold text-slate-900">My Account</h2>
                        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors p-1">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5 space-y-5">

                    {/* Email */}
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                            <User className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{user?.email}</p>
                            <p className="text-xs text-slate-400">Signed in</p>
                        </div>
                    </div>

                    {/* ── Your saved audit ──────────────────────────────
                        Consumer view: a single saved audit per user. We do
                        not surface a multi-audit list or a "+ New audit"
                        button here — that is reserved for a future
                        professional workspace. Legacy users with multiple
                        estate rows still see only their most recently
                        updated one; older rows remain in the database.
                    */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Your saved audit</h3>
                        </div>

                        {loading && (
                            <div className="text-center py-8">
                                <p className="text-sm text-slate-400">Loading...</p>
                            </div>
                        )}

                        {error && (
                            <div className="text-center py-8">
                                <p className="text-sm text-red-500">{error}</p>
                            </div>
                        )}

                        {/* No saved audit yet */}
                        {!loading && !error && !primaryEstate && (
                            <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 text-center">
                                <FileText className="w-8 h-8 text-slate-200 mx-auto mb-3" />
                                <p className="text-sm font-medium text-slate-600 mb-1">No saved audit yet</p>
                                <p className="text-xs text-slate-400 mb-4">Start your probate audit and save your progress to return later.</p>
                                <button
                                    onClick={() => { onClose(); onNewCase(); }}
                                    className="text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 px-5 py-2 rounded-lg transition-colors"
                                >
                                    Start audit
                                </button>
                            </div>
                        )}

                        {/* Single saved audit tile */}
                        {!loading && primaryEstate && (() => {
                            const e = primaryEstate;
                            const unlocked = e.status === 'unlocked';
                            return (
                                <div className="space-y-3">
                                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                                        <div className="p-4">
                                            <div className="flex items-start gap-3 min-w-0">
                                                <div className={`p-2 rounded-lg shrink-0 mt-0.5 ${unlocked ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                                                    {unlocked ? <CheckCircle2 className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-semibold text-sm text-slate-800 truncate">
                                                        {e.deceased_name || 'Unnamed estate'}
                                                    </p>
                                                    {e.date_of_death && (
                                                        <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                                                            <Calendar className="w-3 h-3" />
                                                            {fmtDate(e.date_of_death)}
                                                        </p>
                                                    )}
                                                    <div className="flex items-center gap-2 mt-1.5">
                                                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                                                            unlocked
                                                                ? 'bg-emerald-50 text-emerald-700'
                                                                : 'bg-amber-50 text-amber-700'
                                                        }`}>
                                                            {unlocked ? 'Report unlocked' : 'Draft'}
                                                        </span>
                                                        <span className="text-[10px] text-slate-400">
                                                            Updated {fmtUpdated(e.updated_at)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="border-t border-slate-100 p-3 bg-slate-50/50 space-y-2">
                                            <button
                                                onClick={() => onOpenCase(e.id)}
                                                className="w-full flex items-center justify-center gap-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 px-4 py-2.5 rounded-lg transition-colors"
                                            >
                                                <ArrowRight className="w-4 h-4" />
                                                {unlocked ? 'Open full report' : 'Continue audit'}
                                            </button>

                                            {!unlocked && !freeForAll && (
                                                <button
                                                    onClick={() => handleUpgrade(e.id)}
                                                    className="w-full flex items-center justify-center gap-2 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-4 py-2.5 rounded-lg transition-colors border border-indigo-200"
                                                >
                                                    <Lock className="w-3.5 h-3.5" />
                                                    Unlock full report — {PRICE}
                                                </button>
                                            )}

                                            {unlocked && (
                                                <button
                                                    onClick={() => onOpenCase(e.id)}
                                                    className="w-full flex items-center justify-center gap-2 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-4 py-2.5 rounded-lg transition-colors border border-emerald-200"
                                                >
                                                    <Download className="w-3.5 h-3.5" />
                                                    Download report
                                                </button>
                                            )}
                                        </div>

                                        <div className="border-t border-slate-100 px-4 py-2.5">
                                            <button
                                                onClick={() => { setResetTargetId(e.id); setShowResetConfirm(true); }}
                                                className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-red-500 transition-colors"
                                            >
                                                <RotateCcw className="w-3 h-3" />
                                                Reset this audit
                                            </button>
                                        </div>
                                    </div>

                                    {upgradeError && (
                                        <p className="text-xs text-red-500 text-center mt-1">{upgradeError}</p>
                                    )}
                                </div>
                            );
                        })()}
                    </div>

                    {/* ── Support & trust ──────────────────────────────── */}
                    <div className="space-y-3 pt-2">
                        <a
                            href="mailto:support@estateclarity.co.uk"
                            className="flex items-center gap-2 text-xs text-slate-500 hover:text-indigo-600 transition-colors"
                        >
                            <HelpCircle className="w-3.5 h-3.5" />
                            Need help? Contact support
                        </a>
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                            <Shield className="w-3.5 h-3.5" />
                            Your data is private and securely stored.
                        </div>
                    </div>
                </div>

                {/* Footer — logout */}
                <div className="px-5 py-3 border-t border-slate-100 shrink-0">
                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                    >
                        <LogOut className="w-4 h-4" />
                        Log out
                    </button>
                </div>
            </div>

            {/* Reset confirmation modal */}
            {showResetConfirm && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                                <AlertTriangle className="w-5 h-5 text-red-500" />
                            </div>
                            <h3 className="text-base font-bold text-slate-900">Reset this estate?</h3>
                        </div>
                        <p className="text-sm text-slate-600 leading-relaxed mb-5">
                            This will permanently delete all entered information. This action cannot be undone.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => { setShowResetConfirm(false); setResetTargetId(null); }}
                                className="flex-1 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 px-4 py-2.5 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleReset}
                                disabled={resetting}
                                className="flex-1 text-sm font-medium text-white bg-red-600 hover:bg-red-700 px-4 py-2.5 rounded-lg transition-colors disabled:opacity-50"
                            >
                                {resetting ? 'Deleting...' : 'Delete everything'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
